import { Button, Card, Label } from "semantic-ui-react";
import { getEventMetadata, getSession, useMutationWrapper } from "../helper";
import {
  NEW_LIST_ELEMENTS,
  NEW_LIST_FILTERED_ELEMENTS,
} from "../routes/queries";
import { getNewNextActionFormFields } from "./next_action_form";
import { NewNewFormComponentProps } from "../components/new_form/NewNewFormComponent";
import {
  CREATE_NEW_ELEMENT,
  NEW_COMPLETE_ELEMENT,
  NEW_DELETE_ELEMENT,
  NEW_UPDATE_ELEMENT,
} from "../routes/mutations";
import { LIST_LIFE_DOMAINS } from "../life_domain/routes";
import { NewPageComponent } from "../components/page/NewPageComponent";
import { SettingsCache } from "../cache/SettingsCache";
import { convertPriority } from "../priority/priority";
import { CardDotMenuComponent } from "../components/new_card/dot_menu/CardDotMenuComponent";
import { ReferenceLibraryElementLink } from "src/referencelibrary/ReferenceLibraryElementLink";
import { LinkedReferenceLibraryElementsSection } from "src/referencelibrary/ReferenceLibraryElementSection";

const NextActionCard = (props: {
  data: any;
  setReloadKey: any;
  pageData: any;
  setPageData: any;
}) => {
  const [completeElementMutation] = useMutationWrapper(
    NEW_COMPLETE_ELEMENT.documentNode,
    (data: any) => {
      props.setReloadKey((key: number) => key + 1);
    },
  );

  return (
    <Card fluid key={props.data.id}>
      <Card.Content>
        <div
          style={{ display: "flex", justifyContent: "space-between" }}
          className="card-header"
        >
          <Card.Header style={{ fontSize: 18 }}>
            <b>{props.data.name}</b>
          </Card.Header>
          <CardDotMenuComponent
            setReloadKey={props.setReloadKey}
            pageData={props.pageData}
            setPageData={props.setPageData}
            cardData={props.data}
            props={{
              menuItems: [
                {
                  type: "api_call",
                  label: "Delete",
                  apiCall: NEW_DELETE_ELEMENT,
                  callback: (
                    data: any,
                    setReloadKey: any,
                    pageData: any,
                    setPageData: any,
                  ) => {
                    setReloadKey((key: number) => key + 1);
                  },
                  apiCallVariables: (data: any) => {
                    return {
                      id: props.data.id,
                      session: getSession(),
                      eventMetadata: getEventMetadata(),
                      isChildElement: true,
                    };
                  },
                  showMenuOption: (data) => {
                    return true;
                  },
                },
                {
                  type: "new_form_modal",
                  label: "Edit",
                  formComponentProps: new NewNewFormComponentProps({
                    form: {
                      formFields: getNewNextActionFormFields(),
                      type: "single_form",
                    },
                    startingData: {
                      name: props.data.name,
                      lifeDomainId: props.data.lifeDomain?.id,
                      parentElementId: props.data.parentElement?.id,
                      priority: props.data.priority,
                      id: props.data.id,
                      minuteEstimate: props.data.minuteEstimate ?? undefined,
                    },
                  }),
                  modalHeader: "Edit Next Action",
                  apiCall: NEW_UPDATE_ELEMENT,
                  callback: (data: any) => {
                    props.setReloadKey((key: number) => key + 1);
                  },
                  showMenuOption: (data) => {
                    return true;
                  },
                },
              ],
            }}
          />
        </div>
      </Card.Content>
      <Card.Content>
        {props.data.topItemOfDay && (
          <Label ribbon color="orange">
            Top Item of Day
          </Label>
        )}
        <div className="priority">
          <b>Priority: </b>
          {convertPriority(props.data.priority)}
        </div>
        {props.data.minuteEstimate && (
          <div className="time-estimate">
            <b>Time Estimate: </b>
            {props.data.minuteEstimate} minutes
          </div>
        )}
        {props.data.parentElement && (
          <div className="project">
            <b>Project: </b>
            {props.data.parentElement.name}
          </div>
        )}
        {props.data.lifeDomain && (
          <div className="life-domain">
            <b>Life Domain: </b>
            {props.data.lifeDomain.name}
          </div>
        )}
        {props.data.blocks && props.data.blocks.length > 0 && (
          <div className="blocks">
            <b>Blocking: </b>
            {props.data.blocks.map((block: any) => block.name).join(", ")}
          </div>
        )}
        {props.data.blockedBy && props.data.blockedBy.length > 0 && (
          <div className="blocked-by">
            <b>Blocked By: </b>
            {props.data.blockedBy.map((block: any) => block.name).join(", ")}
          </div>
        )}
      </Card.Content>
      <LinkedReferenceLibraryElementsSection
        data={props.data}
        setReloadKey={props.setReloadKey}
      />
      <Card.Content extra>
        <Button
          floated="right"
          positive
          onClick={() => {
            completeElementMutation({
              variables: {
                id: props.data.id,
                isChildElement: true,
                eventMetadata: getEventMetadata(),
                session: getSession(),
              },
            });
          }}
        >
          Complete
        </Button>
      </Card.Content>
    </Card>
  );
};

export const NewNextActionPage = () => {
  return (
    <>
      <NewPageComponent
        props={{
          type: "list_page",
          query: NEW_LIST_FILTERED_ELEMENTS,
          queryVariables: {
            session: getSession(),
            type: 5,
            filter: {},
          },
          itemName: "Next Action",
          card: NextActionCard,
          creationProps: {
            formComponentProps: new NewNewFormComponentProps({
              form: {
                formFields: getNewNextActionFormFields(),
                type: "single_form",
              },
              startingData: {
                name: "",
                lifeDomainId: undefined,
                parentElementId: undefined,
                priority: SettingsCache.getInstance().getDefaultPriority(),
              },
            }),
            apiCall: CREATE_NEW_ELEMENT,
          },
          filterComponentProps: {
            form: {
              formFields: [
                {
                  type: "query_dropdown",
                  key: "lifeDomainId",
                  label: "Life Domain",
                  apiCall: {
                    documentNode: LIST_LIFE_DOMAINS,
                    key: "listLifeDomains",
                  },
                  apiVariables: {
                    session: getSession(),
                  },
                  noSelectionLabel: "All Life Domains",
                  required: false,
                },
                {
                  type: "query_dropdown",
                  required: false,
                  label: "Parent Project",
                  noSelectionLabel: "All Projects",
                  key: "parentElementId",
                  apiCall: NEW_LIST_ELEMENTS,
                  apiVariables: {
                    session: getSession(),
                    types: [4],
                    appendTypeToName: false,
                  },
                },
              ],
              type: "single_form",
            },
            startingData: {
              lifeDomainId: undefined,
            },
          },
        }}
      />
    </>
  );
};
